import { backButton, init, settingsButton } from "@telegram-apps/sdk";
import Scenes from "../enums/Scenes";
import Game from "./Game";

// Navigation callbacks set by React app
let navigateCallback: ((path: string) => void) | null = null;

export function setNavigateCallback(callback: (path: string) => void) {
  navigateCallback = callback;
}

function navigateToMainMenu() {
  if (navigateCallback) {
    navigateCallback("/menu");
  }
}

function navigateToSettings() {
  if (navigateCallback) {
    navigateCallback("/settings");
  }
}

const MODAL_SCENES = [
  Scenes.PauseScene,
  Scenes.UI_levelUpScene,
  Scenes.UI_treasureScene,
  Scenes.GameOverScene,
];
export default class Router {
  private game: Phaser.Game;
  private currentScene: Scenes | null = null;
  private previousScenes: Scenes[] = [];
  private isNavigating: boolean = false;

  constructor(game: Phaser.Game) {
    this.game = game;
  }

  private backButtonCallback = (): void => {
    if (this.currentScene === Scenes.MainScene) {
      // Do nothing if narration is open
      if (
        document.querySelector("#game-narration-container")?.hasChildNodes()
      ) {
        return;
      }

      this.game.scene.pause(Scenes.MainScene);
      Game.Core.FreezeSnapshot(() => {
        this.game.scene.run(Scenes.PauseScene);
      });

      return;
    } else if (MODAL_SCENES.includes(this.currentScene as Scenes)) {
      return;
    }

    // When back button is pressed, navigate to main menu using React Router
    navigateToMainMenu();
  };

  public init(): void {
    // Initialize TMA SDK
    init();

    // Mount the back button if it's supported
    if (backButton.isSupported()) {
      try {
        backButton.mount();
      } catch (e) {
        console.warn("Failed to mount back button:", e);
      }
    }

    // Add settings button click handler
    if (settingsButton.onClick?.isAvailable?.()) {
      settingsButton.onClick(() => {
        navigateToSettings();
      });
    }

    // By default, start with the main menu scene
    // We could check for any active scenes here and set currentScene accordingly
    const currentActiveScene = this.game.scene.getScenes(true)[0]?.scene
      ?.key as Scenes | undefined;

    if (currentActiveScene) {
      this.currentScene = currentActiveScene;
      this.updateBackButton();
    } else {
      this.navigateToScene(Scenes.MainMenuScene);
    }
  }

  private updateBackButton(): void {
    // Only interact with back button if it's mounted
    if (!backButton.isMounted()) {
      return;
    }

    try {
      // Show back button except on main menu
      if (this.currentScene === Scenes.MainMenuScene) {
        backButton.hide.ifAvailable();
        backButton.offClick.ifAvailable(this.backButtonCallback);
      } else {
        backButton.show.ifAvailable();
        backButton.onClick.ifAvailable(this.backButtonCallback);
      }
    } catch (e) {
      console.warn("Failed to update back button:", e);
    }
  }

  public updateCurrentScene(scene: Scenes): void {
    // If we're changing scenes, save the current scene in history
    if (this.currentScene && this.currentScene !== scene) {
      this.previousScenes.push(this.currentScene);

      // Limit history to 10 entries
      if (this.previousScenes.length > 10) {
        this.previousScenes.shift();
      }
    }

    // Update current scene
    this.currentScene = scene;
  }

  public navigateToScene(
    scene: Scenes,
    params?: Record<string, any>,
    fromSettings: boolean = false
  ): void {
    if (this.isNavigating) return;
    this.isNavigating = true;

    if (!fromSettings) {
      this.updateCurrentScene(scene);

      // Update back button visibility based on current scene
      this.updateBackButton();
    }

    // Handle MainMenuScene specially - use React Router instead of Phaser scene
    if (scene === Scenes.MainMenuScene) {
      // Clear stage data when navigating to main menu
      Game.Core.StageService.clearStageData();

      // Stop all active scenes except UI overlays
      const activeScenes = this.game.scene.getScenes(true);
      activeScenes.forEach((activeScene) => {
        const key = activeScene.scene.key;
        // Don't stop UI overlay scenes
        if (!MODAL_SCENES.includes(key as Scenes)) {
          this.game.scene.stop(key);
        }
      });

      // Navigate to main menu using React Router
      navigateToMainMenu();

      // Reset navigation lock after a short delay
      setTimeout(() => {
        this.isNavigating = false;
      }, 100);
      return;
    }

    // Stop all active scenes except UI overlays
    const activeScenes = this.game.scene.getScenes(true);
    activeScenes.forEach((activeScene) => {
      const key = activeScene.scene.key;
      // Don't stop UI overlay scenes
      if (!MODAL_SCENES.includes(key as Scenes)) {
        this.game.scene.stop(key);
      }
    });

    // Start the new scene
    this.game.scene.start(scene, params);

    // Reset navigation lock after a short delay
    setTimeout(() => {
      this.isNavigating = false;
    }, 100);
  }
}
